import type {
    AiSearchPhase,
    ICommittedRanking,
    ISearchHit,
    SearchErrorKind,
} from "commonComponents/utilities/MainNavigation/AiSearch/types";

/**
 * How long typing has to pause before the dialog searches and asks for a
 * spelling correction, so a word typed in one go costs one search.
 */
export const QUERY_DEBOUNCE_MS = 200;

/**
 * How long a search may run without committing a ranking before the dialog
 * gives up on it and offers a retry. Longer than the slowest single search
 * measured against the service, a long pasted query, so it trips only on a
 * request that has stopped making progress.
 */
export const SEARCH_COMMIT_TIMEOUT_MS = 30_000;

/**
 * The query as it is searched: trimmed, with runs of whitespace collapsed.
 * Two inputs that normalize alike are the same search, so a trailing space
 * does not search again.
 */
export function normalizeQuery(query: string): string {
    return query.trim().replace(/\s+/g, " ");
}

/**
 * Whether `next` continues `previous`: typed on to its end, or backspaced off
 * it. Only then is the previous ranking worth keeping on screen while the next
 * one loads; after a clear or a new word it would describe some other query.
 */
export function isQueryEdit(previous: string, next: string): boolean {
    if (previous.length === 0 || next.length === 0) {
        return false;
    }
    const a = previous.toLowerCase();
    const b = next.toLowerCase();
    return a.startsWith(b) || b.startsWith(a);
}

/**
 * What the results view shows.
 *
 * `rows` is the ranking for what is typed. `held` is the ranking the search in
 * flight will replace, shown dimmed and inert so the list does not blank on
 * every pause in typing. Both are committed rankings, unfiltered: the view
 * applies `isRoutable` itself.
 */
export type ResultsDisplay =
    | { kind: "rows"; ranked: ISearchHit[] }
    | { kind: "held"; ranked: ISearchHit[] }
    | { kind: "searching" }
    | { kind: "empty" }
    | { kind: "error"; errorKind: SearchErrorKind };

export interface IResultsDisplayInput {
    /** The live input, normalized. */
    liveKey: string;
    /** The query the hook last searched. */
    searchQuery: string;
    phase: AiSearchPhase;
    errorKind: SearchErrorKind | null;
    committed: ISearchHit[] | null;
    previous: ICommittedRanking | null;
}

export function resolveResultsDisplay(
    input: IResultsDisplayInput,
    isRoutable: (hit: ISearchHit) => boolean,
): ResultsDisplay {
    const { liveKey, searchQuery, phase, errorKind, committed, previous } = input;

    // The hook's last search is for what is typed.
    if (searchQuery === liveKey) {
        // Ahead of the error: a stream cut after the ranking still delivered it.
        if (committed?.some(isRoutable)) {
            return { kind: "rows", ranked: committed };
        }
        if (errorKind !== null) {
            return { kind: "error", errorKind };
        }
        if (phase === "done" || phase === "empty") {
            // The service commits a ranking on every search it completes, so
            // finishing without one means ranking failed. Whether anything
            // matched is unknown, so saying nothing did would be a guess.
            return committed === null ? { kind: "error", errorKind: "server" } : { kind: "empty" };
        }
    }

    const held: ICommittedRanking | null =
        committed !== null ? { query: searchQuery, hits: committed } : previous;
    if (held !== null && isQueryEdit(held.query, liveKey) && held.hits.some(isRoutable)) {
        return { kind: "held", ranked: held.hits };
    }
    return { kind: "searching" };
}

/** What each failure says. The hook's own strings are for the dropdown. */
export const SEARCH_ERROR_COPY: Record<SearchErrorKind, string> = {
    network: "Couldn't reach search. Check your connection, then retry.",
    server: "Search ran into a problem.",
    timeout: "Search took too long to respond.",
    auth: "Couldn't sign in to search.",
    config: "Search isn't available right now.",
};

/** A missing service URL will not fix itself on a second try. */
export function isRetryableSearchError(kind: SearchErrorKind): boolean {
    return kind !== "config";
}

export interface IResultsAnnouncement {
    /** For the polite status region. */
    status: string;
    /** For the alert region: failures only. */
    alert: string;
}

/**
 * What a screen reader hears as the results change, so the loading, count,
 * empty and failed states are announced rather than only painted.
 */
export function describeResults(
    display: ResultsDisplay,
    query: string,
    corrected: string | null,
    isRoutable: (hit: ISearchHit) => boolean,
): IResultsAnnouncement {
    if (display.kind === "error") {
        return { status: "", alert: SEARCH_ERROR_COPY[display.errorKind] };
    }
    const suggestion = corrected !== null ? ` Did you mean ${corrected}?` : "";
    switch (display.kind) {
        case "rows": {
            const count = display.ranked.filter(isRoutable).length;
            const noun = count === 1 ? "result" : "results";
            return { status: `${count} ${noun}.${suggestion}`, alert: "" };
        }
        case "empty":
            return { status: `Nothing named “${query}”.${suggestion}`, alert: "" };
        default:
            return { status: `Searching…${suggestion}`, alert: "" };
    }
}
